// src/components/trial/lp/trial-beta/sections/multiconnection.tsx
// Ported from the design attachment, lines 2535-2827. The design shipped this section as a
// complete standalone HTML document inside `<iframe srcdoc>` (2537-2820) — flattened here into
// ordinary JSX, with the document's stylesheet namespaced under `.tb-multiconn` (see the long
// "Peça 8" block at the bottom of public/css/trial-beta.css) and its inline script ported to
// ../animation/multiconnection.
//
// Two things only the flattening makes necessary:
//
//   * EVERY id from the nested document is prefixed `tb-mc-`. Inside its own document they could
//     be as generic as `routes`, `direct-path`, `game-status` or `with-ping`; on this page the
//     multi-internet document (Peça 9) is about to arrive with its own ids and the two would
//     silently resolve to each other's elements. `id='multiconnection'` is the one exception —
//     it belongs to the OUTER host page (line 2535), not to the nested document, and it is this
//     section's page anchor.
//   * The title renders as `<h2>`, not the source's `<h1>` (2704): the source could own an `<h1>`
//     because it was a separate document; on this page hero.tsx owns the only one. Tag only —
//     the CSS rule was retagged with it and DOM depth is untouched, which the stylesheet's
//     direct-child rules depend on.
//
// This markup is the section's static, no-JS resting state, verbatim from the source (2698-2742):
// route 0 shows pool[0], the metrics show 168/19ms, the hops show their seeded latencies. The
// animation module rewrites all of it from `strings` the moment the section scrolls into view —
// the same static-markup / imperative-HUD split hero.tsx and hero.ts already use.
import { useTranslation } from 'next-i18next';
import type { CSSProperties } from 'react';

import { MULTICONNECTION_ISP_HOPS, MULTICONNECTION_ROUTES, multiconnectionAnimation } from '../animation/multiconnection';
import { useSectionAnimation } from '../animation/use-section-animation';
import { TRIAL_BETA_ASSETS } from '../assets';

// Source 2717-2723. The seeded hop latencies are the resting frame only: the module recomputes
// all seven from the live ISP total on its first tick (`calculateIspHopLatencies`).
const HOP_SEED_LATENCIES = [8, 13, 10, 13, 12, 12, 12];

// `--delay` staggers each packet along its route (source 2726-2730, `.packet`/`.final-packet`
// read it via `animation-delay:var(--delay)`). React types style values as CSSProperties, which
// has no slot for custom properties, so the cast is the standard escape hatch.
function delayStyle(delay: string): CSSProperties {
  return { '--delay': delay } as CSSProperties;
}

export function Multiconnection() {
  const { t } = useTranslation('components/trial/lp/trial-beta');
  const sectionRef = useSectionAnimation(multiconnectionAnimation);

  return (
    <section id='multiconnection' ref={sectionRef} className='tb-multiconn' aria-labelledby='tb-mc-title' style={{ zIndex: 1 }}>
      <div className='wrap'>
        <header className='section-head'>
          <div className='eyebrow'>{t('multiconnection.eyebrow')}</div>
          <h2 id='tb-mc-title'>
            {t('multiconnection.title')} <span>{t('multiconnection.titleHighlight')}</span>
          </h2>
          <p className='sub'>
            {t('multiconnection.sub')}
            <br className='copy-break' /> {t('multiconnection.subContrast')}
          </p>
        </header>
        <div className='instrument' id='tb-mc-instrument'>
          <div className='mode-switcher'>
            <div className='mode-switch-copy'>
              <strong>{t('multiconnection.mode.title')}</strong>
              <span id='tb-mc-mode-description'>{t('multiconnection.mode.description')}</span>
            </div>
            <div className='mode-switch-actions'>
              <div className='route-count'>
                <span id='tb-mc-active-count'>5 / 5</span>
                <span>{t('multiconnection.mode.routesOnline')}</span>
              </div>
              <button className='mode-toggle' id='tb-mc-toggle' type='button' role='switch' aria-checked aria-label={t('multiconnection.mode.toggleLabel')} aria-describedby='tb-mc-mode-description' aria-controls='tb-mc-topology'>
                <span>{t('multiconnection.mode.off')}</span>
                <span className='toggle-track' aria-hidden='true'>
                  <i />
                </span>
                <span id='tb-mc-mode-state'>{t('multiconnection.mode.on')}</span>
              </button>
            </div>
          </div>
          <div className='compare' aria-label={t('multiconnection.compare.label')}>
            <div className='metric metric--without' role='button' tabIndex={0} aria-pressed={false} aria-controls='tb-mc-topology' data-mode='isp' aria-label={t('multiconnection.compare.withoutAction')}>
              <div className='metric-label'>
                {t('multiconnection.compare.withoutTitle')}
                <span>{t('multiconnection.compare.withoutSubtitle')}</span>
              </div>
              <output className='metric-value' id='tb-mc-without-ping' aria-label={t('multiconnection.compare.withoutPing')}>
                168<small>ms</small>
              </output>
              <div className='metric-details'>
                <span>
                  {t('multiconnection.compare.loss')}
                  <b id='tb-mc-without-loss'>4.6%</b>
                </span>
                <span>
                  {t('multiconnection.compare.jitter')}
                  <b id='tb-mc-without-jitter'>42ms</b>
                </span>
              </div>
            </div>
            <div className='metric metric--with' role='button' tabIndex={0} aria-pressed aria-controls='tb-mc-topology' data-mode='noping' aria-label={t('multiconnection.compare.withAction')}>
              <div className='metric-label'>
                {t('multiconnection.compare.withTitle')}
                <span>{t('multiconnection.compare.withSubtitle')}</span>
              </div>
              <output className='metric-value' id='tb-mc-with-ping' aria-label={t('multiconnection.compare.withPing')}>
                19<small>ms</small>
              </output>
              <div className='metric-details'>
                <span>
                  {t('multiconnection.compare.loss')}
                  <b id='tb-mc-with-loss'>0.0%</b>
                </span>
                <span>
                  {t('multiconnection.compare.jitter')}
                  <b id='tb-mc-with-jitter'>1ms</b>
                </span>
                <span className='connection-node-name'>
                  {t('multiconnection.compare.connectedTo')}
                  <b>Washington 06</b>
                </span>
              </div>
            </div>
          </div>
          <div className='topology' id='tb-mc-topology' aria-label={t('multiconnection.topology.label')}>
            <div className='mobile-flow-label' aria-hidden='true'>
              <span>{t('multiconnection.flow.source')}</span>
              <b>›</b>
              <span id='tb-mc-flow-middle'>{t('multiconnection.flow.middle')}</span>
              <b>›</b>
              <span>{t('multiconnection.flow.destination')}</span>
            </div>
            <div className='node node--user'>
              <div className='node-icon' aria-hidden='true'>
                <svg className='endpoint-glyph computer-glyph' viewBox='0 0 64 64' fill='none'>
                  <path className='glyph-glow' d='M5 14h35v26H5zM45 7h14v47H45z' />
                  <rect className='glyph-shell' x='4.5' y='13.5' width='35' height='26' rx='2.5' />
                  <rect className='glyph-panel' x='8.5' y='17.5' width='27' height='17.5' rx='1' />
                  <path className='glyph-detail' d='M10 33.5l8-8 5 4 6-6 5 4M22 40v7m-7 2h14m-19 4h25' />
                  <rect className='glyph-shell' x='44.5' y='6.5' width='15' height='48' rx='2.5' />
                  <path className='glyph-panel' d='M48.5 11h7v12h-7z' />
                  <path className='glyph-detail' d='M48.5 28h7m-7 4h7m-7 4h7' />
                  <circle className='glyph-led' cx='52' cy='47.5' r='1.65' />
                  <circle className='glyph-led' cx='56' cy='47.5' r='.8' opacity='.48' />
                </svg>
              </div>
              <strong>{t('multiconnection.nodes.userTitle')}</strong>
              <small>
                {t('multiconnection.nodes.userLine')}
                <br />
                Dallas, USA
              </small>
            </div>
            <div className='direct-path' id='tb-mc-direct-path' role='list' aria-label={t('multiconnection.directPath.label')} aria-hidden='true'>
              <i className='isp-packet' aria-hidden='true' />
              <div className='isp-hops'>
                {MULTICONNECTION_ISP_HOPS.map((hop, hopIndex) => (
                  <div className='isp-hop' role='listitem' data-hop={hopIndex} key={hop.name}>
                    <small>{String(hopIndex + 1).padStart(2, '0')}</small>
                    <strong>{hop.name}</strong>
                    <span className='hop-value'>{HOP_SEED_LATENCIES[hopIndex]}ms</span>
                  </div>
                ))}
              </div>
            </div>
            <div className='routes' id='tb-mc-routes' role='list' aria-label={t('multiconnection.routes.label')}>
              {MULTICONNECTION_ROUTES.map((route, routeIndex) => {
                const bridge = route.pool[0];
                return (
                  <div className='route' role='listitem' data-route={routeIndex} key={bridge.name}>
                    <div className='route-track' />
                    <i className='packet' style={delayStyle(route.packetDelays[0])} />
                    <i className='packet' style={delayStyle(route.packetDelays[1])} />
                    <div className='latency-pill'>
                      <span className='access-ms'>{bridge.access}</span>
                      <small>ms</small>
                    </div>
                    <div className='bridge'>
                      <div className='bridge-copy'>
                        <span className='bridge-name'>{bridge.name}</span>
                        <span className='bridge-state'>{t('multiconnection.routes.connected')}</span>
                      </div>
                      <i className='state-dot' />
                      <i className='bridge-scan' />
                    </div>
                    <div className='latency-pill'>
                      <span className='edge-ms'>{bridge.edge}</span>
                      <small>ms</small>
                    </div>
                    <div className='total-pill'>
                      <span className='total-ms'>{bridge.access + bridge.edge}</span>ms
                    </div>
                    <span className='route-status'>{t('multiconnection.routes.offline')}</span>
                  </div>
                );
              })}
            </div>
            <div className='node node--hub'>
              <div className='node-icon' aria-hidden='true'>
                <svg className='endpoint-glyph server-glyph' viewBox='0 0 64 64' fill='none'>
                  <path className='glyph-glow' d='M17 5.5h30l3 3v47l-3 3H17l-3-3v-47z' />
                  <path className='glyph-shell' d='M17 5.5h30l3 3v47l-3 3H17l-3-3v-47z' />
                  <path className='glyph-panel' d='M19 11h26v11H19zM19 26.5h26v11H19zM19 42h26v11H19z' />
                  <path className='glyph-detail' d='M23 15h13m-13 4h8m-8 11.5h13m-13 4h8M23 46h13m-13 4h8' />
                  <circle className='glyph-led' cx='41' cy='16.5' r='1.6' />
                  <circle className='glyph-led' cx='41' cy='32' r='1.6' />
                  <circle className='glyph-led' cx='41' cy='47.5' r='1.6' />
                  <path className='glyph-detail' d='M20 58.5V61m24-2.5V61M20 61h24' />
                </svg>
              </div>
              <strong>{t('multiconnection.nodes.hubTitle')}</strong>
              <small>
                {t('multiconnection.nodes.hubLine')}
                <br />
                Washington 06
              </small>
            </div>
            <div className='final-link' aria-hidden='true'>
              <i className='final-packet' style={delayStyle('0s')} />
              <i className='final-packet' style={delayStyle('-.55s')} />
              <span className='game-latency' id='tb-mc-game-hop'>
                0 ms
              </span>
            </div>
            <div className='node node--game'>
              <div className='node-icon'>
                <span className='game-live' id='tb-mc-game-status' aria-hidden='true'>
                  LIVE
                </span>
                <span className='game-disconnected' aria-hidden='true'>
                  {t('multiconnection.game.lost')}
                  <small>{t('multiconnection.game.frozen')}</small>
                </span>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption -- decorative gameplay loop:
                    muted, no audio track and aria-labelled; the source (2734) shipped it the same way. */}
                <video autoPlay muted loop playsInline preload='metadata' poster={TRIAL_BETA_ASSETS.gameFortnite} aria-label={t('multiconnection.nodes.gameplayLabel')}>
                  <source src={TRIAL_BETA_ASSETS.gameplayLoop} type='video/mp4' />
                </video>
              </div>
              <strong>Fortnite</strong>
              <small>
                {t('multiconnection.nodes.gameLine')}
                <br />
                Washington, D.C.
              </small>
            </div>
          </div>
          <div className='event-bar'>
            <span className='event-kicker' id='tb-mc-event-kicker'>
              {t('multiconnection.event.kicker')}
            </span>
            <div className='event-message' id='tb-mc-event-message' aria-live='polite'>
              {t('multiconnection.event.message')}
            </div>
            <div className='lock-readout'>
              <span id='tb-mc-lock-copy'>{t('multiconnection.lock.copy')}</span>
              <b id='tb-mc-lock-readout'>Dallas 107 · 19ms total</b>
            </div>
          </div>
          <footer className='instrument-foot'>
            <div className='legend' id='tb-mc-legend'>
              <span>
                <i />
                {t('multiconnection.legend.active')}
              </span>
              <span className='offline'>
                <i />
                {t('multiconnection.legend.replacing')}
              </span>
            </div>
            <div className='uptime' id='tb-mc-uptime'>
              {t('multiconnection.uptime.lead')} <b>100%</b> <span aria-hidden='true'>{t('multiconnection.uptime.separator')}</span>
              {t('multiconnection.uptime.middle')}
              <b>{t('multiconnection.uptime.tail')}</b>
            </div>
          </footer>
        </div>
        {/* The source's CTA (2739) preventDefault'd and postMessage'd `noping:open-trial` to the
            host page, because it lived in an iframe and could not reach the signup form itself.
            Flattened, the form is on the same page, so the anchor simply resolves — `#hero-form`
            is the id hero.tsx puts on the signup card. */}
        <div className='cta-row'>
          <a className='cta' href='#hero-form'>
            {t('multiconnection.cta')}
          </a>
        </div>
      </div>
      <i className='seam' aria-hidden='true' />
    </section>
  );
}
